// The palette's recent commands: the ids of commands that ran successfully, kept in this browser
// per organisation, account and screen kind (the Home or a project), newest first. Only ids are
// kept, never a typed query, and a recent command is checked for availability every time the
// palette lists it, so another organisation's permission never revives one. Pure over a storage
// object, so node --test checks it.

import type { Screen } from './palette.ts'

/// How many ids one scope keeps, and how many scopes the store keeps.
export const KEPT_PER_SCOPE = 8
export const KEPT_SCOPES = 24

type Store = Record<string, string[]>

export interface Memory {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/// Who the recents belong to: the organisation's origin and the account, or neither on a device
/// with no sign-in, and the kind of screen.
export function scopeOf(origin: string | null, account: string | null, screen: Screen): string {
  return JSON.stringify([origin ?? '', account ?? '', screen])
}

function read(memory: Memory, key: string): Store {
  try {
    const parsed: unknown = JSON.parse(memory.getItem(key) ?? '{}')
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    const store: Store = {}
    for (const [scope, ids] of Object.entries(parsed)) {
      if (Array.isArray(ids) && ids.every(id => typeof id === 'string')) store[scope] = ids.slice(0, KEPT_PER_SCOPE)
    }
    return store
  } catch {
    return {}
  }
}

/// The ids for a scope, newest first.
export function recentsFor(memory: Memory, key: string, scope: string): string[] {
  return read(memory, key)[scope] ?? []
}

/// Puts an id first in its scope, once, and drops the oldest ids and scopes past the limits.
export function remember(memory: Memory, key: string, scope: string, id: string): void {
  const store = read(memory, key)
  const ids = [id, ...(store[scope] ?? []).filter(x => x !== id)].slice(0, KEPT_PER_SCOPE)
  delete store[scope]
  const scopes = Object.entries(store).slice(-(KEPT_SCOPES - 1))
  try {
    memory.setItem(key, JSON.stringify(Object.fromEntries([...scopes, [scope, ids]])))
  } catch {
    // A full or refused store keeps no recents, which costs only a suggestion.
  }
}
